import React, { createContext, ReactNode, useContext, useState } from 'react';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface VideoConferenceContextValue {
  isOpen: boolean;
  url: string;
  position: Position;
  size: Size;
  minimized: boolean;

  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  setPosition: React.Dispatch<React.SetStateAction<Position>>;
  setSize: React.Dispatch<React.SetStateAction<Size>>;
  setMinimized: React.Dispatch<React.SetStateAction<boolean>>;
}

const VideoConferenceContext = createContext<
  VideoConferenceContextValue | undefined
>(undefined);

interface VideoConferenceProviderProps {
  children: ReactNode;
}

export function VideoConferenceProvider({
  children,
}: VideoConferenceProviderProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const [url, setUrl] = useState('');

  const [position, setPosition] = useState<Position>({
    x: 20,
    y: 20,
  });

  const [size, setSize] = useState<Size>({
    width: 420,
    height: 820,
  });

  const [minimized, setMinimized] = useState(false);

  return (
    <VideoConferenceContext.Provider
      value={{
        isOpen,
        url,
        position,
        size,
        minimized,
        setIsOpen,
        setUrl,
        setPosition,
        setSize,
        setMinimized,
      }}
    >
      {children}
    </VideoConferenceContext.Provider>
  );
}

export function useVideoConference(): VideoConferenceContextValue {
  const context = useContext(VideoConferenceContext);

  if (!context) {
    throw new Error(
      'useVideoConference must be used within a VideoConferenceProvider',
    );
  }

  return context;
}
